// P3 confidence-scorer configuration — single tunable source of truth.
//
// P3 is a REQUEST-TIME ranking aid for the manual reconcile picker (see
// scoreCandidatesForLine in p3Score.ts) — NOT part of the automated matcher. Each
// candidate is scored against the invoice line on four weighted dimensions and binned
// into a confidence band that drives the picker's ordering + "Trafność" pill. All knobs
// live here so the scorer stays pure and the thresholds are tunable in one place.

/** Per-dimension weights. The scorer renormalizes over the *applicable* dimensions, so
 *  an N/A dimension (e.g. no comparable weight on a flat-rate line) drops out of the
 *  denominator. */
export interface ScoreWeights {
  amount: number;
  weight: number;
  date: number;
  ref: number;
}

/** Tolerance ramps: a dimension scores 1.0 at zero difference and decays linearly to
 *  0.0 once the difference reaches the configured max. Relative maxes use an absolute
 *  floor so tiny invoices aren't judged on noise. */
export interface ScoreTolerances {
  /** Amount: relative difference at which the amount sub-score hits 0 (e.g. 0.05 = 5%). */
  amountRelMax: number;
  /** Amount: absolute floor (currency units) below which the relative ramp is widened. */
  amountAbsFloor: number;
  /** Weight: relative difference at which the weight sub-score hits 0. */
  weightRelMax: number;
  /** Weight: absolute floor (tonnes). */
  weightAbsFloor: number;
  /** Date: day difference at which the date sub-score hits 0 (same-month → high score). */
  dateMaxDays: number;
}

/** Confidence-band thresholds (inclusive lower bounds) — drive the picker pill colour. */
export interface ScoreBands {
  /** ≥ this → near-certain fit (green). */
  high: number;
  /** ≥ this (and < high) → likely (blue). */
  medium: number;
  /** ≥ this (and < medium) → plausible (amber). Below → no pill. */
  low: number;
}

export interface ScoreConfig {
  weights: ScoreWeights;
  tolerances: ScoreTolerances;
  bands: ScoreBands;
}

export const DEFAULT_SCORE_CONFIG: ScoreConfig = {
  weights: { amount: 0.40, weight: 0.35, date: 0.15, ref: 0.10 },
  tolerances: {
    amountRelMax: 0.05,
    amountAbsFloor: 1.0,
    weightRelMax: 0.02,
    weightAbsFloor: 0.05,
    dateMaxDays: 60,
  },
  bands: { high: 0.90, medium: 0.70, low: 0.50 },
};

/** Band name for a confidence score, or `null` below the low threshold. */
export type ScoreBandName = 'high' | 'medium' | 'low' | null;

// ── Shared matcher constants (single source of truth) ─────────────────────

/** REC-m7: money tolerance for match verification AND the reconcile report. Both used to
 *  drift (verify 0.015, report 0.01); they are now the SAME value = the repo-wide
 *  MONEY_TOLERANCE (0.01). The subset-sum target match stays GROSZ-EXACT (0.00) by
 *  design — a subset that sums to the exact net is the real set of transactions; a
 *  tolerance there would admit coincidental near-sums as false matches. Verification
 *  (aggregate net/vat/gross across a matched set) still needs ±0.01 to absorb per-line
 *  rounding, so the two are deliberately different. */
export const MATCH_MONEY_TOLERANCE = 0.01;

/** A14: P2 (subset-sum) date window. P1 gates candidates to the invoice's calendar month
 *  (± adjacent for paid). P2 is a blind subset-sum with no reference anchor, so it had NO
 *  lower date bound at all — an arbitrarily old orphan side could subset-sum into a new
 *  invoice. P2 now bounds candidates to [sellDate − P2_LOOKBACK_MONTHS, sellDate + upper]
 *  where the upper bound matches P1 (same/adjacent month). A WIDER lookback than P1 (P1 =
 *  same month) tolerates legitimately-late per-ton billing while still rejecting stale
 *  orphans years old. */
export const P2_LOOKBACK_MONTHS = 3;

// Reference-token patterns + all reference tokenization/matching now live in the single
// reference module — see `REF_PATTERNS`, `extractRefs`, `containsRef`, `extractReferences`
// in `shared/services/invoice/references.ts`.

export function bandFor(confidence: number, bands: ScoreBands): ScoreBandName {
  if (confidence >= bands.high) return 'high';
  if (confidence >= bands.medium) return 'medium';
  if (confidence >= bands.low) return 'low';
  return null;
}
